import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService, type AuthUser } from '../services/auth.service';
import {
  clearStoredAuthTokens,
  getStoredAuthTokens,
  getErrorMessage,
  isAuthExpiredError,
  setStoredAuthTokens,
} from '../services/http';

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    set => ({
      // State
      user: null,
      token: null,
      refreshToken: null,
      loading: false,
      error: null,

      // Actions
      login: async (email: string, password: string) => {
        try {
          set({ loading: true, error: null });

          const response = await authService.login(email, password);

          setStoredAuthTokens(response.access_token, response.refresh_token);

          set({
            user: response.user,
            token: response.access_token,
            refreshToken: response.refresh_token,
            loading: false,
            error: null,
          });
        } catch (error) {
          const errorMessage = getErrorMessage(error, '登录失败，请检查邮箱和密码');
          clearStoredAuthTokens();
          set({
            user: null,
            token: null,
            refreshToken: null,
            loading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          console.warn('Logout request failed:', error);
        } finally {
          clearStoredAuthTokens();
          set({
            user: null,
            token: null,
            refreshToken: null,
            loading: false,
            error: null,
          });
        }
      },

      getCurrentUser: async () => {
        const { accessToken, refreshToken } = getStoredAuthTokens();

        if (!accessToken && !refreshToken) {
          set({ user: null, token: null, refreshToken: null, loading: false });
          return;
        }

        try {
          set({ loading: true, error: null });

          const user = await authService.getCurrentUser();

          // 检查管理员权限，如果不是管理员则清除登录状态
          if (!user.isAdmin) {
            clearStoredAuthTokens();
            set({
              user: null,
              token: null,
              refreshToken: null,
              loading: false,
              error: '您没有管理员权限',
            });
            return;
          }

          const tokens = getStoredAuthTokens();
          set({
            user,
            token: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            loading: false,
            error: null,
          });
        } catch (error) {
          console.error('Get current user failed:', error);

          if (isAuthExpiredError(error)) {
            clearStoredAuthTokens();
            set({
              user: null,
              token: null,
              refreshToken: null,
              loading: false,
              error: null,
            });
            return;
          }

          const tokens = getStoredAuthTokens();
          set(state => ({
            user: state.user,
            token: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            loading: false,
            error: state.user ? null : '登录状态暂时无法验证，请稍后重试',
          }));
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ loading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    }
  )
);
